import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LanguageSwitcher } from '../components/shared';
import './ErrorPage.css';

interface ErrorPageProps {
  errorType?: 'invalid_link' | 'expired_link' | 'unauthorized' | 'gdpr_declined' | 'general';
  message?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ 
  errorType: propErrorType, 
  message 
}) => {
  const [searchParams] = useSearchParams();
  const errorType = propErrorType || searchParams.get('type') || 'general';
  const [language, setLanguage] = useState<'en' | 'el'>('el'); // Default to Greek

  const getErrorContent = () => {
    const content = {
      invalid_link: {
        el: {
          icon: '🔗❌',
          title: 'Μη Έγκυρος Σύνδεσμος',
          description: 'Ο σύνδεσμος που χρησιμοποιήσατε δεν είναι έγκυρος ή έχει τροποποιηθεί.',
          details: [
            'Βεβαιωθείτε ότι χρησιμοποιείτε τον πλήρη σύνδεσμο που σας δόθηκε',
            'Ελέγξτε αν έχετε αντιγράψει σωστά τον σύνδεσμο',
            'Μην τροποποιείτε το περιεχόμενο του συνδέσμου'
          ]
        },
        en: {
          icon: '🔗❌',
          title: 'Invalid Link',
          description: 'The link you used is not valid or has been modified.',
          details: [
            'Make sure you are using the full link that was given to you',
            'Check if you have copied the link correctly',
            'Do not modify the content of the link'
          ]
        }
      },
      expired_link: {
        el: {
          icon: '⏰',
          title: 'Σύνδεσμος Ληγμένος',
          description: 'Ο σύνδεσμος έχει λήξει και δεν μπορεί πλέον να χρησιμοποιηθεί.',
          details: [
            'Οι σύνδεσμοι έχουν διάρκεια ζωής 24 ώρες για λόγους ασφάλειας',
            'Παρακαλώ επικοινωνήστε με τον γιατρό σας για νέο σύνδεσμο',
            'Ο νέος σύνδεσμος θα είναι έγκυρος για άλλες 24 ώρες'
          ]
        },
        en: {
          icon: '⏰',
          title: 'Expired Link',
          description: 'The link has expired and can no longer be used.',
          details: [
            'Links have a 24-hour lifespan for security reasons',
            'Please contact your doctor for a new link',
            'The new link will be valid for another 24 hours'
          ]
        }
      },
      unauthorized: {
        el: {
          icon: '🔒',
          title: 'Μη Εξουσιοδοτημένη Πρόσβαση',
          description: 'Δεν έχετε τα απαραίτητα δικαιώματα για πρόσβαση σε αυτή τη σελίδα.',
          details: [
            'Χρησιμοποιήστε μόνο τον σύνδεσμο που σας δόθηκε',
            'Μην επιχειρήσετε να τροποποιήσετε τη διεύθυνση',
            'Κάθε σύνδεσμος είναι μοναδικός και προσωπικός'
          ]
        },
        en: {
          icon: '🔒',
          title: 'Unauthorized Access',
          description: 'You do not have the necessary permissions to access this page.',
          details: [
            'Use only the link that was given to you',
            'Do not attempt to modify the address',
            'Each link is unique and personal'
          ]
        }
      },
      gdpr_declined: {
        el: {
          icon: '🚫',
          title: 'Συναίνεση Απορριφθείσα',
          description: 'Δεν μπορείτε να συνεχίσετε χωρίς τη συναίνεσή σας για την επεξεργασία δεδομένων σύμφωνα με τους κανόνες GDPR.',
          details: [
            'Η συναίνεσή σας είναι απαραίτητη για την επεξεργασία των ιατρικών δεδομένων',
            'Μπορείτε να επικοινωνήσετε με τον γιατρό σας για περισσότερες πληροφορίες',
            'Χωρίς συναίνεση δεν είναι δυνατή η συμπλήρωση του ερωτηματολογίου'
          ]
        },
        en: {
          icon: '🚫',
          title: 'Consent Declined',
          description: 'You cannot continue without your consent for data processing in accordance with GDPR regulations.',
          details: [
            'Your consent is required for processing medical data',
            'You can contact your doctor for more information',
            'Without consent, completing the questionnaire is not possible'
          ]
        }
      },
      general: {
        el: {
          icon: '⚠️',
          title: 'Σφάλμα Πρόσβασης',
          description: message || 'Για λόγους ασφάλειας το link κλειδώθηκε. Επικοινωνήστε με τον γιατρό.',
          details: []
        },
        en: {
          icon: '⚠️',
          title: 'Access Error',
          description: message || 'For security reasons the link has been locked. Contact your doctor.',
          details: []
        }
      }
    };

    return content[errorType as keyof typeof content]?.[language] || content.general[language];
  };

  const errorContent = getErrorContent();

  return (
    <div className="error-page">
      <div className="error-container">
        {/* Language Selector using universal component */}
        <div className="error-language-selector">
          <LanguageSwitcher
            selectedLanguage={language}
            onLanguageChange={setLanguage}
            variant="default"
            className="error-page-lang-switcher"
          />
        </div>

        <div className="error-icon">{errorContent.icon}</div>
        
        <div className="error-content">
          <h1 className="error-title">{errorContent.title}</h1>
          <p className="error-description">{errorContent.description}</p>
          
          {errorContent.details.length > 0 && (
            <div className="error-details">
              <h3>{language === 'el' ? 'Τι μπορείτε να κάνετε:' : 'What you can do:'}</h3>
              <ul>
                {errorContent.details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;